import { Segmented } from 'antd';
import { useMemo, useState } from 'react';
import ReactECharts, { AXIS_TEXT, GRID_LINE } from '../charts/echarts';
import { fmtCompact, fmtDay, fmtDec, fmtHours, fmtKc, fmtNum } from '../format';
import styles from '../style.module.scss';

type Metric = 'kc' | 'hours' | 'active';

const METRIC_OPTIONS: { label: string; value: Metric }[] = [
  { label: 'Kim cương', value: 'kc' },
  { label: 'Giờ LIVE', value: 'hours' },
  { label: 'Creator LIVE', value: 'active' },
];

const ACCENT = '#C2337D';
const MA_COLOR = '#1B2150';
const HOURS_COLOR = '#1F5FA8';
const ACTIVE_COLOR = '#0F7A46';

const fmtOf = (metric: Metric) =>
  metric === 'kc' ? fmtKc : metric === 'hours' ? fmtHours : (v: number) => `${fmtNum(v)} Creator`;

const baseAxis = (labels: string[]) => ({
  type: 'category',
  data: labels,
  boundaryGap: false,
  axisLine: { lineStyle: { color: GRID_LINE } },
  axisLabel: { color: AXIS_TEXT },
});

const valueAxis = (formatter?: (v: number) => string) => ({
  type: 'value',
  min: 0,
  splitLine: { lineStyle: { color: GRID_LINE } },
  axisLabel: { color: AXIS_TEXT, formatter },
});

const compactAxis = (v: number) => fmtCompact(v);
const decAxis = (v: number) => fmtDec(v);
const hoursAxis = (v: number) => `${fmtDec(v)}h`;

interface OverviewProps {
  days: string[];
  diamonds: number[];
  ma7: (number | null)[];
  liveHours: number[];
  activeCreators: number[];
  anomalies: { day: string; z: number }[];
}

/** Tổng quan: đường KC/ngày + MA7 (điểm đột biến), chuyển sang Giờ LIVE / Creator LIVE. */
export const DailyLine = ({
  days,
  diamonds,
  ma7,
  liveHours,
  activeCreators,
  anomalies,
}: OverviewProps) => {
  const [metric, setMetric] = useState<Metric>('kc');

  const option = useMemo(() => {
    const labels = days.map(fmtDay);
    const fmt = fmtOf(metric);
    const main = metric === 'kc' ? diamonds : metric === 'hours' ? liveHours : activeCreators;
    const color = metric === 'kc' ? ACCENT : metric === 'hours' ? HOURS_COLOR : ACTIVE_COLOR;
    const idx = new Map(days.map((d, i) => [d, i] as [string, number]));
    const series: any[] = [
      {
        name: METRIC_OPTIONS.find((m) => m.value === metric)?.label,
        type: 'line',
        data: main,
        smooth: true,
        symbolSize: 5,
        lineStyle: { width: 2, color },
        itemStyle: { color },
        areaStyle: { color, opacity: 0.08 },
        markPoint:
          metric === 'kc' && anomalies.length
            ? {
                symbol: 'pin',
                symbolSize: 38,
                itemStyle: { color: '#E8B04B' },
                label: { formatter: '!', color: '#1D1D2E', fontWeight: 700 },
                tooltip: {
                  trigger: 'item',
                  formatter: (p: any) => `${p.name}: ${fmtKc(p.value)} KC`,
                },
                data: anomalies
                  .filter((a) => idx.has(a.day))
                  .map((a) => {
                    const v = diamonds[idx.get(a.day) as number];
                    return {
                      name: `Đột biến ${fmtDay(a.day)}`,
                      coord: [fmtDay(a.day), v],
                      value: v,
                    };
                  }),
              }
            : undefined,
      },
    ];
    if (metric === 'kc') {
      series.push({
        name: 'Trung bình 7 ngày',
        type: 'line',
        data: ma7,
        smooth: true,
        symbol: 'none',
        connectNulls: false,
        lineStyle: { width: 2, type: 'dashed', color: MA_COLOR },
        itemStyle: { color: MA_COLOR },
      });
    }
    return {
      animation: false,
      grid: { left: 8, right: 16, top: 36, bottom: 8, containLabel: true },
      legend: { top: 0, left: 0, textStyle: { color: AXIS_TEXT } },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: number | null) => (v === null || v === undefined ? '—' : fmt(v)),
      },
      xAxis: baseAxis(labels),
      yAxis: valueAxis(metric === 'kc' ? compactAxis : decAxis),
      series,
    };
  }, [days, diamonds, ma7, liveHours, activeCreators, anomalies, metric]);

  return (
    <section className={styles.card} aria-label="Biểu đồ theo ngày">
      <div className={styles.cardHead}>
        <h2 className={styles.cardTitle}>Theo ngày</h2>
        <Segmented<Metric>
          size="small"
          options={METRIC_OPTIONS}
          value={metric}
          onChange={(v) => setMetric(v as Metric)}
        />
      </div>
      {metric === 'kc' && anomalies.length > 0 && (
        <p className={styles.muted}>
          Ghim vàng (!) = ngày đột biến so với các ngày khác trong tháng.
        </p>
      )}
      <ReactECharts option={option} style={{ height: 300, width: '100%' }} />
    </section>
  );
};

interface DualProps {
  days: string[];
  diamonds: number[];
  liveSeconds: number[];
}

/** Drawer: KC (trục trái, cột) & giờ LIVE (trục phải, đường) theo ngày của một Creator. */
export const DualAxisLine = ({ days, diamonds, liveSeconds }: DualProps) => {
  const option = useMemo(
    () => ({
      animation: false,
      grid: { left: 8, right: 8, top: 36, bottom: 8, containLabel: true },
      legend: { top: 0, left: 0, textStyle: { color: AXIS_TEXT } },
      tooltip: { trigger: 'axis' },
      xAxis: { ...baseAxis(days.map(fmtDay)), boundaryGap: true },
      yAxis: [
        valueAxis(compactAxis),
        {
          ...valueAxis(hoursAxis),
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: 'Kim cương',
          type: 'bar',
          data: diamonds,
          itemStyle: { color: ACCENT, borderRadius: [3, 3, 0, 0] },
          barMaxWidth: 14,
          tooltip: { valueFormatter: (v: number) => fmtKc(v) },
        },
        {
          name: 'Giờ LIVE',
          type: 'line',
          yAxisIndex: 1,
          data: liveSeconds.map((s) => Math.round((s / 3600) * 10) / 10),
          smooth: true,
          symbolSize: 5,
          lineStyle: { width: 2, color: HOURS_COLOR },
          itemStyle: { color: HOURS_COLOR },
          tooltip: { valueFormatter: (v: number) => fmtHours(v) },
        },
      ],
    }),
    [days, diamonds, liveSeconds]
  );
  return <ReactECharts option={option} style={{ height: 260, width: '100%' }} />;
};

export default DailyLine;
